<script setup lang="ts">
import useUserStore from "~/stores/user";
import useEventStore from "~/stores/event";
import useAsync from "~/hooks/use-async";
import ClipAPI from "~/services/clip";
import useSegment from "~/hooks/use-segment";
import analytics from "~/utils/segment-analytics";
import useEventSelect from "~/hooks/events/use-event-select";
import { EVENT_STATUS, EVENT_TYPE } from "~/constants";

export interface EventInfoProps {
  item: IModelEvent;
}
const props = defineProps<EventInfoProps>();

const { t } = useI18n();
const route = useRoute();
const appConfig = useRuntimeConfig().public;
const segment = useSegment();
const userStore = useUserStore();
const eventStore = useEventStore();

const isClippingModeEnabled = appConfig.APP_CUSTOMER.web_sdk.enable_clipping;
const isDownloadClipAllowed = appConfig.APP_CUSTOMER.web_sdk.enabled_downloading_clip;
const isTaggingModeEnabled = appConfig.APP_CUSTOMER.web_sdk.enable_tagging_mode;

const isLoggedIn = computed(() => Boolean(userStore.user));
const isEventStartingSoon = computed(() => eventStore.isEventStartingSoon);
const isClip = computed(() => props.item.eventType === EVENT_TYPE.USER_CLIP);
const isHighlight = computed(() => props.item.eventType === EVENT_TYPE.USER_HIGHLIGHT || props.item.eventType === EVENT_TYPE.HIGHLIGHT);
const isNotUpcoming = computed(() => props.item.eventStatus !== EVENT_STATUS.UPCOMING);
const hasPlayerError = computed(() => eventStore.playerState?.error !== null);
const videoType = computed(() => eventStore.playerState?.panoMode ? "pano" : "hd");
const isExternal = computed(() => eventStore.data?.isExternal);
const canDownloadClip = computed(() => isClip.value && isLoggedIn.value && isClippingModeEnabled && isDownloadClipAllowed);
const canCreateClip = computed(() => {
  return isClippingModeEnabled
  && isLoggedIn.value
  && isNotUpcoming.value
  && !isClip.value
  && !isHighlight.value
  && props.item.isEventAvailableForUser
  && props.item.isEventAvailableInUserRegion;
});
const canCreateTag = computed(() => {
  return isTaggingModeEnabled
  && isLoggedIn.value
  && isNotUpcoming.value
  && !isClip.value
  && !isHighlight.value
  && props.item.isEventAvailableForUser
  && props.item.isEventAvailableInUserRegion
  && !isExternal.value;
});

const onClickClip = () => {
  eventStore.isCreatingClip = true;
  eventStore.tags.isCreatingTag = false;
};
const onClickTag = () => {
  eventStore.tags.isCreatingTag = true;
  eventStore.isCreatingClip = false;
};
const onSuccess = () => {
  const payload = {
    event: props.item,
    videoType: videoType.value,
    navigationPage: route.path,
  };
  segment.track(
    analytics.DownloadVideoStarted.event,
    analytics.DownloadVideoStarted.getData(payload),
  );
};

const { isSelected, loading: loadingSelectEvent, toggleEventSelect: onClickSelect } = useEventSelect(props.item.id);
const { loading: loadingDowloadClip, run: downloadClip } = useAsync(ClipAPI.download, { onSuccess });
</script>

<template>
  <BaseButton
    v-if="canDownloadClip"
    :disabled="hasPlayerError || isEventStartingSoon || loadingDowloadClip"
    class="text-link !no-underline"
    type="secondary"
    name="download-clip"
    link
    @click="downloadClip(props.item)"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconDownload class="inline-block" fill="currentColor" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ t('button.download') }}</span>
    </div>
  </BaseButton>
  <BaseButton
    v-if="canCreateClip"
    :disabled="hasPlayerError || isEventStartingSoon"
    class="text-link !no-underline"
    type="secondary"
    name="create-clip"
    link
    @click="onClickClip"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconClip class="inline-block" fill="currentColor" :active="true" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ t('labels.clip', 1) }}</span>
    </div>
  </BaseButton>
  <BaseButton
    v-if="canCreateTag"
    :disabled="hasPlayerError || isEventStartingSoon"
    class="text-link !no-underline"
    type="secondary"
    name="create-tag"
    link
    @click="onClickTag"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconTag class="inline-block" fill="currentColor" :active="isSelected" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ t('labels.tag', 1) }}</span>
    </div>
  </BaseButton>
  <EventInfoShare :title="props.item.title" :can-share-on-schedule="isNotUpcoming" />
  <BaseButton
    v-if="isLoggedIn"
    :disabled="loadingSelectEvent"
    class="text-link !no-underline"
    type="secondary"
    name="bookmark"
    link
    @click="onClickSelect"
  >
    <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
      <IconBookmark class="inline-block" fill="currentColor" :active="isSelected" :width="16" :height="16" />
      <span class="text-[var(--base-secondary-text-color)]">{{ isSelected ? t('labels.bookmark_remove') : t('labels.bookmark_add') }}</span>
    </div>
  </BaseButton>
</template>
